const buildLinePairs = (lines) => {
  const pairs = [];
  const pairedLines = [];

  const orderLines = lines.filter((line) => line.lineType === 'ORDER');
  const returnLines = lines.filter((line) => line.lineType === 'RETURN');
  const linesWithManualAssignment = lines.filter((line) => line.manualAssignmentLine);

  // Starts with the preceeding lines (those that have manual assignment)
  linesWithManualAssignment.forEach((line) => {
    const matchingLine = lines.find((l) => l.lineId === line.manualAssignmentLine);

    // If we don't find a match, we don't push to pairs to avoid duplication
    if (matchingLine) {
      pairedLines.push(line.lineId, matchingLine.lineId);

      // Makes sure that we handle manual assignment in both RETURN and ORDER lines
      const returnLine = line.lineType === 'RETURN' ? line : matchingLine;
      const orderLine = line.lineType === 'ORDER' ? line : matchingLine;

      pairs.push({ return: returnLine, order: orderLine });
    }
  });

  // Loops through Return Lines since they're the base of the UI
  returnLines.forEach((line) => {
    if (pairedLines.includes(line.lineId)) {
      return;
    }

    // Extract the prefix and suffix to find the target line id
    const prefix = line.lineId.substr(0, 6);
    const suffix = parseFloat(line.lineId.substring(6)) + 1;
    const targetLineId = `${prefix}${suffix}`;

    // Find the pair based on the line id logic
    const matchingLine = lines.find((l) => l.lineId === targetLineId);

    if (matchingLine && !pairedLines.includes(matchingLine.lineId)) {
      pairedLines.push(line.lineId, matchingLine.lineId);
      pairs.push({ return: line, order: matchingLine });
    } else {
      pairs.push({ return: line, order: null });
    }

    // If there's no matching line, a null will be placed in the order side of the pair
  });

  // Loops through order lines that have no pairs and push them to the end of the list
  orderLines.forEach((line) => {
    if (pairedLines.includes(line.lineId)) {
      return;
    }

    pairs.push({ return: null, order: line });
  });

  return pairs;
};

const getGroupInstance = (line) => ({
  description: line.partDescription || '',
  extendedPrice: 0,
  returnedTotal: 0,
  returned: 0,
  shippedTotal: 0,
  shipped: 0,
  pairs: [],
});

const getValidLines = (lines) => lines.filter((line) => line.partNumber.match(/^1(0|2)0/));

export const buildOrderTable = (data) => {
  if (!data) {
    return {};
  }

  const lines = getValidLines(data.getAccount.getRmaOrder.lines || []);
  const linePairs = buildLinePairs(lines);

  const groups = linePairs.reduce((groups, pair) => {
    const { return: returnLine, order: orderLine } = pair;

    // Group lines by part number
    const partNumber = returnLine?.partNumber || orderLine?.partNumber;
    const group = groups[partNumber] || getGroupInstance(returnLine || orderLine);

    group.pairs.push(pair);

    // Increase counters for return lines
    if (returnLine) {
      group.returnedTotal += 1;

      if (returnLine.lineStatus === 'CLOSED') {
        group.returned += 1;
      }
    }

    // Increase counters for order lines
    if (orderLine) {
      group.shippedTotal += 1;
      group.extendedPrice += orderLine.extendedSellPrice || 0;

      if (orderLine.lineStatus === 'CLOSED') {
        group.shipped += 1;
      }
    }

    return { ...groups, [partNumber]: group };
  }, {});

  return groups;
};

export const mapOrderStatus = (statusCode) =>
  ({
    AWAIT_BILLING: 'Closed',
    AWAIT_SHIP: 'Awaiting Shipping',
    BACKORDERED: 'Awaiting Shipping',
    CANCELED: 'Canceled',
    CHANGE_PENDING: 'Not Started',
    CLOSED: 'Closed',
    CREATED: 'Not Started',
    DOO_CANCEL_REQUESTED: 'Canceled',
    DOO_CHANGED: 'Not Started',
    DOO_REQ_CREATED: 'Not Started',
    NOT_STARTED: 'Not Started',
    PARTIAL_BACK: 'Awaiting Shipping',
    PARTIAL_PICK: 'Awaiting Shipping',
    PICKED: 'Awaiting Shipping',
    SCHEDULED: 'Awaiting Shipping',
    SCH_MANUAL: 'Awaiting Shipping',
    SHIPPED: 'Closed',
    USER_ACTION: 'Not Started',
    WAITING: 'Not Started',
    UNKNOWN: 'Unknown',
  }[statusCode || 'UNKNOWN'] || 'Unknown');

export const mapReturnStatus = (statusCode) =>
  ({
    AWAIT_BILLING: 'Closed',
    AWAIT_RECEIVING: 'Awaiting Receiving',
    CANCELED: 'Canceled',
    CHANGE_PENDING: 'Awaiting Receiving',
    CLOSED: 'Closed',
    CREATED: 'Awaiting Receiving',
    DELIVERED: 'Closed',
    DOO_CANCEL_REQUESTED: 'Canceled',
    DOO_CHANGED: 'Awaiting Receiving',
    NOT_STARTED: 'Awaiting Receiving',
    USER_ACTION: 'Awaiting Receiving',
    WAITING: 'Awaiting Receiving',
  }[statusCode || 'INVALID'] || 'Unknown');
